/**
 * useCallSession — a live two-person video call, in the world.
 *
 * PORTED FROM THE WEB'S `useCollabSession` (HypeTunes_Web_2026/src/hooks/) and
 * kept deliberately close to it. Khari, 2026-08-04: "We have placed the ability
 * to have peer to peer live video chat in the website, and it includes the
 * ability to appear as your character. I want to add that to the phone in the
 * game… see the website for reference, and add it here."
 *
 * Same API routes, same `/collab` signaling namespace, same short codes, same
 * X2 avatar camera — so a call placed from the phone and a session started in
 * the studio are the SAME session, and a fix to either travels by being the
 * same code rather than by being remembered twice. The differences are three:
 *
 *   surface: 'world'  the invite's notification opens the WORLD with the phone
 *                     ringing, instead of the studio
 *   arrangement sync  unused here (a call has no timeline) — the plumbing stays
 *                     because removing it is how two copies start to diverge
 *   shareUrl          points at the world
 *
 * ORIGINAL HEADER FOLLOWS.
 *
 * useCollabSession — live two-person studio session.
 *
 * Face-to-face WebRTC video between the host (whose arrangement is the source
 * of truth) and one guest, with the arrangement itself synced over the API's
 * `/collab` socket namespace. The server only relays; media is peer-to-peer.
 *
 * Pairing model (mirrors the TV feature): the host creates a session over REST
 * (POST /api/collab/session → short code), both sides join the socket room by
 * code. The side already in the room initiates the WebRTC offer when a peer
 * joins — deterministic, so there's no signaling glare.
 *
 * The hook stays arrangement-agnostic: the page hands it `getSnapshot()` (what
 * to send a newly-joined peer) and `onRemoteState(state)` (apply what the peer
 * sent). `sendState` is trailing-throttled so pointer-move drag streams become
 * ~12 updates/sec on the wire while the last frame always lands.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_BASE } from '../config';
import { startAvatarCamera } from '../services/avatarCamera';

const SEND_THROTTLE_MS = 80;
const FALLBACK_ICE = [{ urls: ['stun:stun.l.google.com:19302'] }];

export default function useCallSession({ token, getSnapshot, onRemoteState }) {
  // idle | starting | waiting | connecting | live | ended | error
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [role, setRole] = useState(null);        // 'host' | 'guest'
  const [peer, setPeer] = useState(null);        // { socketId, userId, handle, name, role }
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mediaNote, setMediaNote] = useState('');
  const [invited, setInvited] = useState(null);   // { userId, handle, name } once an invite is sent
  const [inviteNote, setInviteNote] = useState(''); // why an invite didn't go out (session still fine)
  // "Appear as your avatar" (Reactor X2): off | preparing | live | error
  const [avatarCam, setAvatarCam] = useState({ status: 'off', avatarId: null, name: null });

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const mediaGenRef = useRef(0);      // bumped on teardown; stale acquisitions self-stop
  const mediaPromiseRef = useRef(null); // in-flight getUserMedia (single-flight)
  const sessionGenRef = useRef(0);    // bumped on teardown; cancels in-flight start/join
  const appearAsRef = useRef(null);   // { avatarId, name } | null — chosen before entering
  const avatarCamRef = useRef(null);  // handle from startAvatarCamera ({ stop })
  const placeholderRef = useRef(null); // blank canvas track ({ track, stop }) — see privacy note below
  // What actually goes to the peer: raw camera stream, or avatar video + real
  // mic once X2 is live. localStreamRef always stays the RAW stream (it owns
  // the hardware tracks and feeds X2 as the source).
  const outgoingStreamRef = useRef(null);
  const codeRef = useRef('');
  const roleRef = useRef(null);
  const pendingIceRef = useRef([]);
  const iceServersRef = useRef(FALLBACK_ICE);
  const throttleRef = useRef({ timer: 0, last: 0, queued: null });

  // Callbacks live in refs so socket handlers always see the latest closure.
  const getSnapshotRef = useRef(getSnapshot);
  getSnapshotRef.current = getSnapshot;
  const onRemoteStateRef = useRef(onRemoteState);
  onRemoteStateRef.current = onRemoteState;

  // ── teardown helpers ─────────────────────────────────────
  const closePeer = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch { /* already closed */ }
      pcRef.current = null;
    }
    pendingIceRef.current = [];
    setRemoteStream(null);
    setPeer(null);
  }, []);

  // ── KEEP THE CAMERA AWAKE (iOS) ───────────────────────────────────────────
  //
  // Khari, 2026-08-06, X2 on a phone: "I see myself briefly load, and then the
  // avatar loads in, but I never drive it... it seems to drop the connection of
  // me driving the model/render."
  //
  // X2 renders REMOTELY: we publish the raw camera track up to it and a
  // re-rendered character track comes back. The moment that output arrives we
  // point the preview at it (`setLocalStream(combined)`) — which is right for
  // the PIP, since it should show what the peer sees, but it leaves the RAW
  // track with no on-screen sink. Desktop keeps capturing regardless. Mobile
  // Safari suspends a camera track that nothing is rendering, so the frames we
  // are publishing dry up and the character freezes: it loaded, and then you
  // stopped driving it.
  //
  // So the raw stream keeps its own 1px sink for as long as we hold the camera.
  // Deliberately NOT `display:none` or `visibility:hidden` — to iOS an
  // undisplayed element is the same as no element, which is the bug. It has to
  // be laid out and playing, just too small and too transparent to see.
  const keepAliveRef = useRef(null);
  const keepCameraAwake = useCallback((stream) => {
    if (!stream || typeof document === 'undefined') return;
    let el = keepAliveRef.current;
    if (!el) {
      el = document.createElement('video');
      el.muted = true;
      el.autoplay = true;
      el.playsInline = true;
      el.setAttribute('playsinline', '');   // the attribute is what iOS reads
      el.setAttribute('muted', '');         // autoplay without a gesture
      Object.assign(el.style, {
        position: 'fixed', bottom: '0', left: '0', width: '1px', height: '1px',
        opacity: '0.01', pointerEvents: 'none', zIndex: '-1',
      });
      document.body.appendChild(el);
      keepAliveRef.current = el;
    }
    if (el.srcObject !== stream) el.srcObject = stream;
    el.play().catch(() => { /* it is a sink, not a performance */ });
  }, []);
  const releaseCameraKeepAlive = useCallback(() => {
    const el = keepAliveRef.current;
    if (!el) return;
    try { el.pause(); el.srcObject = null; el.remove(); } catch { /* already gone */ }
    keepAliveRef.current = null;
  }, []);

  const stopMedia = useCallback(() => {
    // Invalidate any getUserMedia still in flight — if it resolves after this
    // teardown, acquireMedia sees the stale generation and stops the stream
    // immediately instead of leaving the camera light on with no owner.
    mediaGenRef.current += 1;
    releaseCameraKeepAlive();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
  }, [releaseCameraKeepAlive]);

  const teardown = useCallback((nextPhase = 'idle') => {
    sessionGenRef.current += 1; // a Cancel/End mid-setup must actually cancel
    avatarCamRef.current?.stop();
    avatarCamRef.current = null;
    placeholderRef.current?.stop();
    placeholderRef.current = null;
    appearAsRef.current = null;
    outgoingStreamRef.current = null;
    setAvatarCam({ status: 'off', avatarId: null, name: null });
    closePeer();
    stopMedia();
    const t = throttleRef.current;
    if (t.timer) { clearTimeout(t.timer); t.timer = 0; t.queued = null; }
    if (socketRef.current) {
      try { socketRef.current.disconnect(); } catch { /* already gone */ }
      socketRef.current = null;
    }
    codeRef.current = '';
    roleRef.current = null;
    setCode('');
    setRole(null);
    setInvited(null);
    setInviteNote('');
    setPhase(nextPhase);
  }, [closePeer, stopMedia]);

  useEffect(() => () => teardown('idle'), [teardown]);

  // ── WebRTC ───────────────────────────────────────────────
  const sendSignal = useCallback((to, data) => {
    socketRef.current?.emit('rtc_signal', { to, data });
  }, []);

  const createPeerConnection = useCallback((peerSocketId, initiator) => {
    closePeer();
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pcRef.current = pc;

    const outgoing = outgoingStreamRef.current || localStreamRef.current;
    if (outgoing) {
      outgoing.getTracks().forEach((track) => pc.addTrack(track, outgoing));
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(peerSocketId, { type: 'ice', candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (stream) setRemoteStream(stream);
    };
    pc.onconnectionstatechange = () => {
      if (pc !== pcRef.current) return;
      if (pc.connectionState === 'connected') setPhase('live');
      // Transient drops go back to "connecting"; a peer leaving is handled by
      // the peer_left event, not here.
      if (pc.connectionState === 'disconnected') setPhase((p) => (p === 'live' ? 'connecting' : p));
    };

    if (initiator) {
      (async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendSignal(peerSocketId, { type: 'offer', sdp: pc.localDescription });
        } catch (err) {
          console.error('[collab] offer failed', err);
        }
      })();
    }
    return pc;
  }, [closePeer, sendSignal]);

  const handleSignal = useCallback(async ({ from, data }) => {
    try {
      if (data.type === 'offer') {
        // Late create for the joiner side (or a re-offer after reconnect).
        const pc = pcRef.current || createPeerConnection(from, false);
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        for (const c of pendingIceRef.current) {
          try { await pc.addIceCandidate(c); } catch { /* stale candidate */ }
        }
        pendingIceRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendSignal(from, { type: 'answer', sdp: pc.localDescription });
      } else if (data.type === 'answer') {
        if (!pcRef.current) return;
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
        for (const c of pendingIceRef.current) {
          try { await pcRef.current.addIceCandidate(c); } catch { /* stale candidate */ }
        }
        pendingIceRef.current = [];
      } else if (data.type === 'ice' && data.candidate) {
        if (pcRef.current?.remoteDescription) {
          await pcRef.current.addIceCandidate(data.candidate);
        } else {
          pendingIceRef.current.push(data.candidate);
        }
      }
    } catch (err) {
      console.error('[collab] signal handling failed', err);
    }
  }, [createPeerConnection, sendSignal]);

  // ── media ────────────────────────────────────────────────
  const acquireMedia = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    // Single-flight: concurrent callers share one getUserMedia, so a double
    // start can never orphan a second (never-stopped) camera stream.
    if (mediaPromiseRef.current) return mediaPromiseRef.current;
    const gen = mediaGenRef.current;
    mediaPromiseRef.current = (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: true,
        });
        if (mediaGenRef.current !== gen) {
          // The session was torn down while the permission prompt was up.
          stream.getTracks().forEach((t) => t.stop());
          return null;
        }
        localStreamRef.current = stream;
        outgoingStreamRef.current = stream;
        // Bound BEFORE anything can repoint the preview — see keepCameraAwake.
        keepCameraAwake(stream);
        setLocalStream(stream);
        setMicOn(true);
        setCamOn(true);
        setMediaNote('');
        return stream;
      } catch (err) {
        // The session is still useful without a camera — the arrangement syncs
        // regardless — so flag it rather than failing the whole thing.
        setMediaNote('Camera/mic unavailable — joining without video.');
        return null;
      } finally {
        mediaPromiseRef.current = null;
      }
    })();
    return mediaPromiseRef.current;
  }, [keepCameraAwake]);

  // ── avatar camera (Reactor X2) ───────────────────────────
  // PRIVACY RULE: once someone chooses to appear as an avatar, their real
  // camera must NEVER reach the peer — not before the swap is ready, and not
  // as an automatic fallback if it fails. The outgoing video is a blank
  // placeholder until X2 is live; failure keeps the placeholder and only an
  // explicit revealRealCamera() (a user click) ever sends the real feed.

  /** A blank video track the peer sees while (or instead of) the avatar. */
  const makePlaceholder = useCallback(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    const draw = () => { ctx.fillStyle = '#0b0b12'; ctx.fillRect(0, 0, canvas.width, canvas.height); };
    draw();
    const stream = canvas.captureStream(2);
    // Some browsers only emit captured frames on canvas writes — keep a slow
    // heartbeat so the peer's tile doesn't stall as "no media".
    const interval = setInterval(draw, 500);
    const track = stream.getVideoTracks()[0];
    return {
      track,
      stop() { clearInterval(interval); try { track.stop(); } catch { /* gone */ } },
    };
  }, []);

  /** Swap the placeholder in as the outgoing video (setup start, X2 death). */
  const hideBehindPlaceholder = useCallback(() => {
    placeholderRef.current?.stop();
    placeholderRef.current = makePlaceholder();
    const raw = localStreamRef.current;
    outgoingStreamRef.current = new MediaStream([
      placeholderRef.current.track,
      ...(raw ? raw.getAudioTracks() : []),
    ]);
    if (raw) setLocalStream(raw); // your own preview: the real you, local-only
    const sender = pcRef.current?.getSenders?.().find((s) => s.track?.kind === 'video');
    if (sender) sender.replaceTrack(placeholderRef.current.track).catch(() => {});
  }, [makePlaceholder]);

  /** The ONLY path that puts the real camera on the wire after choosing an avatar. */
  const revealRealCamera = useCallback(() => {
    appearAsRef.current = null;
    // Unmask the nametag on the other side too — face and name change together.
    socketRef.current?.emit('appearance_changed', { appearingAs: null });
    avatarCamRef.current?.stop();
    avatarCamRef.current = null;
    placeholderRef.current?.stop();
    placeholderRef.current = null;
    const raw = localStreamRef.current;
    outgoingStreamRef.current = raw;
    if (raw) setLocalStream(raw);
    const rawTrack = raw?.getVideoTracks()[0] || null;
    const sender = pcRef.current?.getSenders?.().find((s) => s.track?.kind === 'video');
    if (sender && rawTrack) sender.replaceTrack(rawTrack).catch(() => {});
    setAvatarCam({ status: 'off', avatarId: null, name: null });
  }, []);

  /**
   * Kick off the X2 swap for the chosen avatar. Runs alongside the session —
   * the call starts on the raw camera and hot-swaps (RTCRtpSender.replaceTrack,
   * no renegotiation) the moment the avatar feed arrives.
   */
  const beginAvatarCamera = useCallback(async (rawStream) => {
    const appearAs = appearAsRef.current;
    const sourceTrack = rawStream?.getVideoTracks()[0];
    if (!appearAs || !sourceTrack) return;
    const gen = sessionGenRef.current;
    setAvatarCam({ status: 'preparing', avatarId: appearAs.avatarId, name: appearAs.name });
    try {
      const handle = await startAvatarCamera({
        token,
        avatarId: appearAs.avatarId,
        sourceTrack,
        onOutput: (stream, track) => {
          if (gen !== sessionGenRef.current) return;
          placeholderRef.current?.stop();
          placeholderRef.current = null;
          const combined = new MediaStream([track, ...rawStream.getAudioTracks()]);
          outgoingStreamRef.current = combined;
          setLocalStream(combined); // the PIP shows what the peer sees: the avatar
          const sender = pcRef.current?.getSenders?.().find((s) => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(track).catch(() => {});
          setAvatarCam({ status: 'live', avatarId: appearAs.avatarId, name: appearAs.name });
        },
        onEnded: () => {
          if (gen !== sessionGenRef.current) return;
          // The avatar feed died — go dark, never to the real camera.
          hideBehindPlaceholder();
          setAvatarCam({ status: 'error', avatarId: appearAs.avatarId, name: appearAs.name });
        },
      });
      if (gen !== sessionGenRef.current) { handle.stop(); return; }
      avatarCamRef.current = handle;
    } catch (err) {
      if (gen !== sessionGenRef.current) return;
      console.error('[collab] avatar camera failed', err);
      // The placeholder is already on the wire — stay hidden, report, and let
      // the user decide whether to reveal their real camera.
      setAvatarCam({ status: 'error', avatarId: appearAs.avatarId, name: appearAs.name });
    }
  }, [token, hideBehindPlaceholder]);

  /**
   * PUT THE MASK BACK ON — the return trip revealRealCamera never had.
   *
   * Khari, 2026-08-04: "'show my real camera' should be a toggle. once I press
   * it, I can't get back currently." On the web this is a one-way door because
   * a studio session picks an identity at the door and keeps it; on a phone call
   * you flick between showing your face and hiding behind the character, and a
   * one-way reveal makes the first press unrepeatable.
   *
   * ORDER IS THE PRIVACY RULE, exactly as at entry: go dark FIRST, then start
   * the swap. Never raw camera → avatar, which would put your real face on the
   * wire for the seconds the renderer takes to wake up.
   */
  const appearAsAvatar = useCallback((av) => {
    if (!av?.avatarId) return;
    const raw = localStreamRef.current;
    if (!raw) return;                       // no camera to drive the swap with
    avatarCamRef.current?.stop();           // a second press must not stack renderers
    avatarCamRef.current = null;
    appearAsRef.current = { avatarId: av.avatarId, name: av.name };
    socketRef.current?.emit('appearance_changed', { appearingAs: av.name || null });
    hideBehindPlaceholder();
    beginAvatarCamera(raw);
  }, [hideBehindPlaceholder, beginAvatarCamera]);

  const toggleMic = useCallback(() => {
    const tracks = localStreamRef.current?.getAudioTracks() || [];
    const next = !tracks.some((t) => t.enabled);
    tracks.forEach((t) => { t.enabled = next; });
    setMicOn(next);
  }, []);

  const toggleCam = useCallback(() => {
    const tracks = localStreamRef.current?.getVideoTracks() || [];
    const next = !tracks.some((t) => t.enabled);
    tracks.forEach((t) => { t.enabled = next; });
    setCamOn(next);
  }, []);

  // ── socket ───────────────────────────────────────────────
  const connectAndJoin = useCallback((joinCode) => {
    const socketUrl = API_BASE || window.location.origin;
    const socket = io(`${socketUrl}/collab`, {
      auth: { token },
      path: '/socket.io',
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;
    codeRef.current = joinCode;

    socket.on('connect', () => {
      // First connect and every reconnect: (re)claim our seat. On reconnect
      // our socket id changed, so drop the old peer connection — the other
      // side will re-offer when it sees us join again. The presented identity
      // rides along so the peer's nametag shows the ARTIST, never the account.
      closePeer();
      socket.emit('join_session', {
        code: joinCode,
        appearingAs: appearAsRef.current?.name || null,
      });
    });

    socket.on('session_joined', ({ code: c, role: r, peers }) => {
      setCode(c);
      setRole(r);
      roleRef.current = r;
      if (peers.length > 0) {
        // Someone is already here — they initiate; we wait for the offer.
        setPeer(peers[0]);
        setPhase('connecting');
      } else {
        setPhase('waiting');
      }
    });

    socket.on('peer_joined', (p) => {
      setPeer(p);
      setPhase('connecting');
      createPeerConnection(p.socketId, true);
      // The newcomer needs the arrangement as it stands right now.
      const snapshot = getSnapshotRef.current?.();
      if (snapshot) socket.emit('studio_state', { state: snapshot });
    });

    socket.on('rtc_signal', handleSignal);

    socket.on('studio_state', ({ state }) => {
      onRemoteStateRef.current?.(state);
    });

    socket.on('peer_appearance', ({ socketId, appearingAs }) => {
      setPeer((p) => (p && p.socketId === socketId ? { ...p, appearingAs } : p));
    });

    socket.on('peer_left', () => {
      closePeer();
      setPhase('waiting');
    });

    socket.on('session_ended', () => teardown('ended'));

    socket.on('collab_error', ({ message }) => {
      setError(message || 'Something went wrong.');
      teardown('error');
    });

    socket.on('connect_error', (err) => {
      setError(err?.message || 'Could not connect.');
      teardown('error');
    });
  }, [token, closePeer, createPeerConnection, handleSignal, teardown]);

  const loadIceConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/collab/config`);
      const d = await r.json();
      if (Array.isArray(d.iceServers) && d.iceServers.length) iceServersRef.current = d.iceServers;
    } catch { /* fallback STUN already set */ }
  }, []);

  // ── public actions ───────────────────────────────────────
  /**
   * Host: create a session and sit in it waiting for the guest. Passing
   * `inviteHandle` also sends that user a notification (in-app + push) with
   * the code and a Join button — an invite failure never blocks the session.
   * `appearAs` ({ avatarId, name }) enters the room as that avatar (X2 swap).
   */
  const start = useCallback(async ({ inviteHandle, appearAs } = {}) => {
    if (phase !== 'idle' && phase !== 'ended' && phase !== 'error') return;
    setError('');
    setPhase('starting');
    appearAsRef.current = appearAs || null;
    const gen = sessionGenRef.current;
    try {
      const [res, , media] = await Promise.all([
        fetch(`${API_BASE}/api/collab/session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          // `surface: 'world'` is what makes the invite ring a phone in the
          // city instead of opening the studio. Same session either way.
          body: JSON.stringify({ surface: 'world', ...(inviteHandle ? { inviteHandle } : {}) }),
        }),
        loadIceConfig(),
        acquireMedia(),
      ]);
      if (gen !== sessionGenRef.current) return; // cancelled while setting up
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.code) throw new Error(d.error || 'Could not start a session.');
      setInvited(d.invited || null);
      setInviteNote(d.inviteError || '');
      if (media && appearAsRef.current) {
        // Privacy: the peer connection must NEVER carry the raw camera when an
        // avatar was chosen — go dark first, then become the avatar.
        hideBehindPlaceholder();
        beginAvatarCamera(media);
      }
      connectAndJoin(d.code);
    } catch (err) {
      if (gen !== sessionGenRef.current) return;
      setError(err.message);
      teardown('error');
    }
  }, [phase, token, loadIceConfig, acquireMedia, hideBehindPlaceholder, beginAvatarCamera, connectAndJoin, teardown]);

  /** Guest: join an existing session by code. `appearAs` mirrors start(). */
  const join = useCallback(async (joinCode, { appearAs } = {}) => {
    const normalized = String(joinCode || '').trim().toUpperCase();
    if (!normalized) return;
    if (phase !== 'idle' && phase !== 'ended' && phase !== 'error') return;
    setError('');
    setPhase('starting');
    appearAsRef.current = appearAs || null;
    const gen = sessionGenRef.current;
    try {
      const [res, , media] = await Promise.all([
        fetch(`${API_BASE}/api/collab/session/${normalized}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        loadIceConfig(),
        acquireMedia(),
      ]);
      if (gen !== sessionGenRef.current) return; // cancelled while setting up
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Session not found or expired.');
      if (d.full) throw new Error('This session already has two people in it.');
      if (media && appearAsRef.current) {
        // Privacy: the peer connection must NEVER carry the raw camera when an
        // avatar was chosen — go dark first, then become the avatar.
        hideBehindPlaceholder();
        beginAvatarCamera(media);
      }
      connectAndJoin(normalized);
    } catch (err) {
      if (gen !== sessionGenRef.current) return;
      setError(err.message);
      teardown('error');
    }
  }, [phase, token, loadIceConfig, acquireMedia, hideBehindPlaceholder, beginAvatarCamera, connectAndJoin, teardown]);

  /** Leave (guest) or shut down (host — ends it for both). */
  const leave = useCallback(() => {
    if (roleRef.current === 'host') socketRef.current?.emit('end_session');
    else socketRef.current?.emit('leave_session');
    teardown('idle');
  }, [teardown]);

  /** Broadcast the arrangement — trailing-throttled for drag streams. */
  const sendState = useCallback((state) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const t = throttleRef.current;
    const now = Date.now();
    if (now - t.last >= SEND_THROTTLE_MS && !t.timer) {
      t.last = now;
      socket.emit('studio_state', { state });
    } else {
      t.queued = state;
      if (!t.timer) {
        t.timer = setTimeout(() => {
          t.timer = 0;
          t.last = Date.now();
          if (t.queued && socketRef.current?.connected) {
            socketRef.current.emit('studio_state', { state: t.queued });
            t.queued = null;
          }
        }, SEND_THROTTLE_MS - (now - t.last));
      }
    }
  }, []);

  // The invited artist (if any) rides the link so the recipient's appearance
  // picker can lead with that avatar.
  const shareUrl = code
    ? `${window.location.origin}${window.location.pathname}?call=${code}${invited?.avatarId ? `&as=${invited.avatarId}` : ''}`
    : '';

  return {
    phase,
    live: phase === 'live',
    active: phase !== 'idle' && phase !== 'ended' && phase !== 'error',
    error,
    mediaNote,
    invited,
    inviteNote,
    avatarCam,
    code,
    shareUrl,
    role,
    peer,
    localStream,
    remoteStream,
    appearAsAvatar,
    micOn,
    camOn,
    start,
    join,
    leave,
    toggleMic,
    toggleCam,
    revealRealCamera,
    sendState,
  };
}
